import React from 'react';
import { useMutation } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle2, Clock, RefreshCw, Wrench } from 'lucide-react';
import { Link } from 'react-router-dom';
import { provisionAppInstance, runProvisionDoctor } from '../api';

type ProvisionStatus = 'queued' | 'running' | 'retrying' | 'completed' | 'failed' | string;

export type BuilderProvisionState = {
  status?: ProvisionStatus | null;
  stage?: string | null;
  attempt?: number | null;
  job_id?: string | null;
  error?: string | null;
  next_attempt_at?: string | null;

  // Optional timestamps (best-effort; backend may add/rename over time)
  queued_at?: string | null;
  running_at?: string | null;
  completed_at?: string | null;
  failed_at?: string | null;
  updated_at?: string | null;
  created_at?: string | null;
};

function fmtTime(v: any): string {
  if (!v) return '—';
  const d = new Date(String(v));
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString();
}

function statusBadge(status: ProvisionStatus | null | undefined) {
  const s = (status || 'unknown').toLowerCase();
  const base = 'text-xs';
  if (s === 'completed') return <Badge className={`${base} bg-green-100 text-green-800 hover:bg-green-100`}>completed</Badge>;
  if (s === 'failed') return <Badge className={`${base} bg-red-100 text-red-800 hover:bg-red-100`}>failed</Badge>;
  if (s === 'running') return <Badge className={`${base} bg-blue-100 text-blue-800 hover:bg-blue-100`}>running</Badge>;
  if (s === 'queued') return <Badge className={`${base} bg-gray-100 text-gray-700 hover:bg-gray-100`}>queued</Badge>;
  if (s === 'retrying') return <Badge className={`${base} bg-amber-100 text-amber-800 hover:bg-amber-100`}>retrying</Badge>;
  return <Badge className={`${base} bg-gray-100 text-gray-700 hover:bg-gray-100`}>{String(status || 'unknown')}</Badge>;
}

export function ProvisioningStatusCard({
  appId,
  appStatus,
  provisioningError,
  provision,
  onRefresh,
}: {
  appId: string;
  appStatus?: string | null;
  provisioningError?: string | null;
  provision?: BuilderProvisionState | null;
  onRefresh?: () => void;
}) {
  const p = provision || {};
  const status = (p.status || 'unknown') as ProvisionStatus;

  const doctor = useMutation({
    mutationFn: async () => runProvisionDoctor(appId),
    onSuccess: () => onRefresh?.(),
  });

  const rerun = useMutation({
    mutationFn: async () => provisionAppInstance(appId),
    onSuccess: () => onRefresh?.(),
  });

  const now = Date.now();
  const sinceIso = new Date(now - 24 * 60 * 60 * 1000).toISOString();
  const untilIso = new Date(now).toISOString();
  const provisioningEventsHref =
    `/ops/mcp-events?agent_id=${encodeURIComponent('app-provisioner')}` +
    `&workflow=${encodeURIComponent('provision_app')}` +
    `&app_id=${encodeURIComponent(appId)}` +
    `&since=${encodeURIComponent(sinceIso)}` +
    `&until=${encodeURIComponent(untilIso)}` +
    `&auto=1`;

  const hasAnyDetails = Boolean(
    p.status || p.stage || p.attempt != null || p.job_id || p.error || p.next_attempt_at ||
    p.queued_at || p.running_at || p.completed_at || p.failed_at || p.updated_at || p.created_at
  );

  return (
    <Card className="border border-gray-200">
      <CardContent className="p-6 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-600" />
              <h3 className="font-semibold text-gray-900">Provisioning</h3>
              {statusBadge(status)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              App status: <span className="font-mono text-gray-700">{String(appStatus || '—')}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onRefresh?.()}
              title="Refresh app + settings"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {(provisioningError || p.error) ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700">
            <div className="flex items-center gap-2 font-medium">
              <AlertTriangle className="w-4 h-4" />
              Error
            </div>
            <div className="mt-2 whitespace-pre-wrap break-words">
              {String(p.error || provisioningError)}
            </div>
          </div>
        ) : null}

        {!hasAnyDetails ? (
          <div className="text-sm text-gray-500">
            No `settings.builder.provision` state published yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            <Info label="stage" value={p.stage || '—'} mono />
            <Info label="attempt" value={p.attempt != null ? String(p.attempt) : '—'} mono />
            <Info label="job_id" value={p.job_id || '—'} mono />
            <Info label="next_attempt_at" value={fmtTime(p.next_attempt_at)} />
            <Info label="queued_at" value={fmtTime(p.queued_at)} />
            <Info label="running_at" value={fmtTime(p.running_at)} />
            <Info label="completed_at" value={fmtTime(p.completed_at)} />
            <Info label="failed_at" value={fmtTime(p.failed_at)} />
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => doctor.mutate()}
            disabled={doctor.isPending}
            title="POST /api/apps/:id/provision/doctor"
          >
            <Wrench className="w-4 h-4 mr-2" />
            {doctor.isPending ? 'Running doctor…' : 'Run Doctor'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => rerun.mutate()}
            disabled={rerun.isPending}
            title="POST /api/apps/:id/provision?async=true"
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            {rerun.isPending ? 'Re-running…' : 'Re-run provisioning'}
          </Button>
          <Link
            to={provisioningEventsHref}
            className="text-sm border rounded px-3 py-1 hover:bg-gray-50"
            title="Search provisioning workflow events (Timescale)"
          >
            View provisioning events
          </Link>
        </div>

        {(doctor.isError || rerun.isError) ? (
          <div className="text-xs text-red-700">
            {(doctor.error as any)?.message || (rerun.error as any)?.message || 'Request failed'}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function Info({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-md border bg-gray-50 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`text-gray-900 ${mono ? 'font-mono break-all' : ''}`}>{value}</div>
    </div>
  );
}

